import { useState, useEffect, useMemo, useCallback } from "react";
import { FaCheck, FaEdit } from "react-icons/fa";
import { RiDeleteBin6Line } from "react-icons/ri";
import { MdKeyboardArrowLeft, MdKeyboardArrowRight } from "react-icons/md";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import ExportOptions from "../../../ExportOptions";
import ConfirmActionModal from "../../../ConfirmDeleteModal";
import TableSkeleton from "../../../TableSkeleton";
import OfficeDepositModal from "./OfficeDepositModal";
import PageHeading from "../../../PageHeading";
import DynamicSelect from "../../../DynamicSelect";

const OfficeDepositList = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedRows, setSelectedRows] = useState([]);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [depositToEdit, setDepositToEdit] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [deposits, setDeposits] = useState([]); 
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [filterType, setFilterType] = useState("monthly");
  const [startDate, setStartDate] = useState(new Date().toISOString().split("T")[0]);
  const [endDate, setEndDate] = useState(new Date().toISOString().split("T")[0]);
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());

  const itemsPerPage = 10;
  
  // API Configuration
  const API_URL = "https://api.muktihospital.com/api/hospital/office-deposit";
  const API_KEY = "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079";

  const filterOptions = [
    { value: "daily", label: "Daily" },
    { value: "monthly", label: "Monthly" },
    { value: "yearly", label: "Yearly" },
  ];

  // FETCH DATA
const fetchDeposits = useCallback(async () => {
    setLoading(true);
    try {
      const params = { type: filterType };

      if (filterType === 'daily') {
         params.startDate = startDate;
         params.endDate = endDate;
      } else if (filterType === 'monthly') {
         const [y, m] = selectedMonth.split('-');
         params.year = y;
         params.month = m;
      } else if (filterType === 'yearly') {
         params.year = selectedYear;
      }

      const response = await axios.get(API_URL, {
        headers: { "x-api-key": API_KEY },
        params: params
      });

      if (response.data && Array.isArray(response.data.deposits)) {
        setDeposits(response.data.deposits);
      } else {
        setDeposits([]);
      }
    } catch (error) {
      console.error("Error fetching deposits:", error);
      toast.error("Failed to load deposits.");
      setDeposits([]);
    } finally {
      setLoading(false);
    }
  }, [filterType, startDate, endDate, selectedMonth, selectedYear, refreshTrigger]);

 useEffect(() => {
    fetchDeposits();
  }, [fetchDeposits]);

  // Filter Logic
  const filteredDeposits = useMemo(() => {
    return deposits.filter((item) => {
      const query = searchQuery.toLowerCase().trim();
      if (!query) return true;

      const name = item.name ? item.name.toLowerCase() : "";
      const sector = item.sector ? item.sector.toLowerCase() : "";
      const code = item.depositCode ? item.depositCode.toLowerCase() : "";

      return name.includes(query) || sector.includes(query) || code.includes(query);
    });
  }, [deposits, searchQuery]);

  const totalPages = Math.ceil(filteredDeposits.length / itemsPerPage);

  const currentData = filteredDeposits
    .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
    .map((item, index) => ({
      ...item,
      displayId: (currentPage - 1) * itemsPerPage + index + 1,
    }));

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  const handlePageChange = (page) => {
    if (page > 0 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedRows(currentData.map((item) => item.id));
    } else {
      setSelectedRows([]);
    }
  };

  const handleRowSelect = (id) => {
    setSelectedRows((prevSelectedRows) =>
      prevSelectedRows.includes(id)
        ? prevSelectedRows.filter((rowId) => rowId !== id)
        : [...prevSelectedRows, id]
    );
  };

  // DELETE FUNCTION
  const handleDelete = async (ids) => {
    try {
      for (const id of ids) {
        await axios.delete(`${API_URL}/delete/${id}`, {
          headers: { "x-api-key": API_KEY },
        });
      }
      toast.success("Deposit record(s) deleted successfully!");
      setSelectedRows([]);
      setDeleteModalOpen(false);
      setRefreshTrigger((prev) => prev + 1);
    } catch (error) {
      console.error("Error deleting deposit:", error);
      toast.error("Failed to delete deposit.");
    }
  };

  const handleEdit = (item) => {
    setDepositToEdit(item);
    setModalOpen(true);
  };

  // SAVE FUNCTION
  const handleSaveDeposit = async (savedItem, isEditMode) => {
    try {
      if (isEditMode) {
        await axios.put(`${API_URL}/edit/${savedItem.id}`, savedItem, {
          headers: { "x-api-key": API_KEY },
        });
        toast.success("Deposit updated successfully!");
      } else {
        await axios.post(`${API_URL}/add`, savedItem, {
          headers: { "x-api-key": API_KEY },
        });
        toast.success("Deposit added successfully!");
      }
      setModalOpen(false);
      setDepositToEdit(null);
      setRefreshTrigger((prev) => prev + 1);
    } catch (error) {
      console.error("Error saving deposit:", error);
      const errorMsg = error.response?.data?.error || "Failed to save deposit.";
      toast.error(errorMsg);
    }
  };

  const toggleDropdown = () => setDropdownOpen((prev) => !prev);

  // Export Headers
  const headers = ["Serial No", "Date", "Deposit Code", "Name", "Sector", "Amount", "Note"];
  const dataMapper = (item) =>
    [
      item.displayId,
      new Date(item.createdAt).toLocaleDateString('en-GB'),
      item.depositCode,
      item.name,
      item.sector,
      item.amount,
      item.note
    ].join("\t");

  const skeletonColumns = [
    { width: "40px", height: "16px", className: "mx-3" },
    { width: "100px", height: "16px", className: "px-3" },
    { width: "120px", height: "16px", className: "px-3" },
    { width: "100px", height: "16px", className: "px-3" },
    { width: "80px", height: "16px", className: "px-3" },
    { width: "150px", height: "16px", className: "px-3" },
    { width: "80px", height: "16px", className: "ml-auto" },
  ];

  const breadcrumbs = useMemo(
    () => [
      { label: "Dashboard", url: "/" },
      { label: "Office Accounts" },
      { label: "Office Deposit" },
    ],
    []
  );

  return (
    <div>
      <ToastContainer />
      <div className="print-hidden">
        <PageHeading title="Office Deposit" breadcrumbs={breadcrumbs} />
      </div>
      <div className="bg-white shadow-sm rounded-lg">
        {/* Header Section */}
        <div className="flex flex-wrap gap-3 flex-col sm:flex-row md:items-center justify-between border-b border-dashed border-M-text-color/50 p-4 print-hidden">
          <div className="flex items-center justify-between gap-3">
              <div className="inline-flex items-center gap-3 shrink-0">
            <h2 className="text-base font-medium text-gray-700 flex items-center gap-2">
              Office Deposits List
            </h2>
            {selectedRows.length > 0 && (
              <button
                onClick={() => setDeleteModalOpen(true)}
                className="size-7 bg-M-secondary-color/10 text-M-secondary-color hover:bg-M-secondary-color hover:text-white transition-all duration-300 text-base rounded-md inline-flex items-center justify-center"
                title="Delete selected rows"
              >
                <RiDeleteBin6Line />
              </button>
            )}
          </div>
          <input
              type="text"
              className="max-w-48 w-full p-2 border rounded-md outline-none focus:ring-0 font-inter font-medium text-sm text-M-heading-color"
              placeholder="Search by name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-start sm:justify-end gap-3 w-full sm:w-auto">
            
            {/* --- FILTERS UI --- */}
            <div className="flex items-center gap-2 flex-wrap">
                <div className="w-32">
                  <DynamicSelect 
                    options={filterOptions} 
                    value={filterOptions.find(o => o.value === filterType)} 
                    onChange={o => setFilterType(o.value)} 
                    isSearchable={false}
                    placeholder="Filter"
                  />
                </div>

                {filterType === "daily" && (
                  <>
                      <div className="flex items-center gap-2">
                        <label className="text-sm font-medium text-gray-600">From:</label>
                        <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="p-2 border rounded-md text-sm outline-none w-32" />
                      </div>
                      <div className="flex items-center gap-2">
                        <label className="text-sm font-medium text-gray-600">To:</label>
                        <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="p-2 border rounded-md text-sm outline-none w-32" />
                      </div>
                  </>
                )}
                {filterType === "monthly" && (
                  <div className="flex items-center gap-2">
                      <input type="month" value={selectedMonth} onChange={e => setSelectedMonth(e.target.value)} className="p-2 border rounded-md text-sm outline-none" />
                  </div>
                )}
                {filterType === "yearly" && (
                    <div className="flex items-center gap-2">
                      <input type="number" value={selectedYear} onChange={e => setSelectedYear(e.target.value)} className="p-2 border rounded-md text-sm outline-none w-24" min="2000" max="2100" />
                    </div>
                )}
            </div>
            <div className="flex items-center justify-between gap-3 sm:w-auto w-full">
              <button
                onClick={() => {
                  setDepositToEdit(null);
                  setModalOpen(true);
                }}
                className="bg-[#73CB64] hover:bg-M-primary-color default-button shrink-0 px-4 py-2 text-sm"
              >
                + Add Deposit
              </button>
              <ExportOptions
                data={currentData}
                dropdownOpen={dropdownOpen}
                toggleDropdown={toggleDropdown}
                title="Office Deposits"
                headers={headers}
                dataMapper={dataMapper}
              />
            </div>
          </div>
        </div>

        {/* Table Section */}
        <div>
          <div className="overflow-x-auto print-wrapper">
            {loading ? (
              <div className="print-hidden">
                <table className="min-w-[600px] w-full divide-y divide-gray-200 print-table">
                  <tbody>
                    <TableSkeleton
                      rowCount={itemsPerPage}
                      columns={skeletonColumns}
                      baseColor="#e5e7eb"
                      highlightColor="#f3f4f6"
                    />
                  </tbody>
                </table>
              </div>
            ) : filteredDeposits.length === 0 ? (
              <div className="p-5 text-center text-gray-600 print-hidden">
                No deposits found.
              </div>
            ) : (
              <div className="print-wrapper">
                <table className="min-w-[600px] w-full divide-y divide-gray-200 print-table">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="text-left print-hidden-in-print">
                        <div className="relative left-3 -top-[2px]">
                          <input
                            type="checkbox"
                            className="hidden peer print-hidden"
                            onChange={handleSelectAll}
                            checked={
                                currentData.length > 0 &&
                                currentData.every((item) => selectedRows.includes(item.id))
                            }
                            id="selectAll"
                          />
                          <span className="h-4 w-4 border flex-none border-slate-400 rounded inline-flex items-center justify-center ltr:mr-3 rtl:ml-3 transition-all duration-150 bg-slate-100 peer-checked:bg-M-primary-color peer-checked:ring-1 peer-checked:ring-M-primary-color peer-checked:ring-offset-1 absolute top-[6px] left-0 z-0 print-hidden">
                            <FaCheck className="text-slate-100" />
                          </span>
                          <label
                            htmlFor="selectAll"
                            className="cursor-pointer font-jost font-normal text-base text-slate-200 relative z-10 pl-6 print-hidden"
                          ></label>
                        </div>
                      </th>
                      <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">Serial No</th>
                      <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">Date</th>
                      <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">Deposit Code</th>
                      <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">Deposit Person</th>
                      <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">Sector</th>
                      <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">Amount</th>
                      <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">Note</th>
                      <th className="text-right uppercase font-inter font-medium text-sm text-M-heading-color p-3 print-hidden-in-print">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentData.map((item, index) => (
                      <tr key={item.id} className="border-b hover:bg-gray-50">
                        <td className="text-left print-hidden-in-print">
                           <div className="relative left-3 -top-[3px]">
                            <input
                              type="checkbox"
                              id={`row-${index}`}
                              className="hidden peer"
                              checked={selectedRows.includes(item.id)}
                              onChange={() => handleRowSelect(item.id)}
                            />
                            <span className="h-4 w-4 border flex-none border-slate-400 rounded inline-flex items-center justify-center ltr:mr-3 rtl:ml-3 transition-all duration-150 bg-slate-100 peer-checked:bg-M-primary-color peer-checked:ring-1 peer-checked:ring-M-primary-color peer-checked:ring-offset-1 absolute top-[6px] left-0 z-0 print-hidden">
                              <FaCheck className="text-slate-100" />
                            </span>
                             <label
                              htmlFor={`row-${index}`}
                              className="cursor-pointer font-jost font-normal text-base text-slate-200 relative z-10 pl-6 print-hidden"
                             ></label>
                           </div>
                        </td>
                        <td className="text-left font-inter font-normal text-sm text-M-heading-color py-5 px-3">
                          {item.displayId}
                        </td>
                        <td className="text-left font-inter font-normal text-sm text-M-heading-color py-5 px-3 whitespace-nowrap">
                          {new Date(item.createdAt).toLocaleDateString("en-GB")} 
                        </td>
                        <td className="text-left font-inter font-medium text-sm text-M-heading-color py-5 px-3">
                          {item.depositCode}
                        </td>
                        <td className="text-left font-inter font-medium text-sm text-M-heading-color py-5 px-3 capitalize">
                          {item.name}
                        </td>
                        <td className="text-left font-inter font-normal text-sm text-M-heading-color py-5 px-3">
                          {item.sector}
                        </td>
                        <td className="text-left font-inter font-bold text-sm text-M-heading-color py-5 px-3">
                          {item.amount}
                        </td>
                        <td className="text-left font-inter font-normal text-sm text-gray-500 py-5 px-3 max-w-[200px] truncate">
                          {item.note || "-"}
                        </td>
                        <td className="p-3 text-right space-x-2 print-hidden-in-print">
                          <button
                            onClick={() => handleEdit(item)}
                            className="size-7 rounded-full bg-M-primary-color/10 text-M-primary-color text-sm inline-flex items-center justify-center hover:bg-M-primary-color hover:text-white transition-all duration-300"
                            title="Edit"
                          >
                            <FaEdit />
                          </button>
                          <button
                            onClick={() => {
                              setSelectedRows([item.id]);
                              setDeleteModalOpen(true);
                            }}
                            className="size-7 rounded-full bg-M-secondary-color/10 text-M-secondary-color text-sm inline-flex items-center justify-center hover:bg-M-secondary-color hover:text-white transition-all duration-300"
                            title="Delete"
                          >
                            <RiDeleteBin6Line />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          
          {/* Pagination Section */}
          {totalPages > 1 && (
            <div className="p-5 text-end print-hidden">
               <div className="inline-flex items-center justify-between w-full">
                <p className="text-sm text-gray-600">
                    Records: {currentPage * itemsPerPage - itemsPerPage + 1} to {Math.min(currentPage * itemsPerPage, filteredDeposits.length)} of {filteredDeposits.length}
                </p>
                <ul className="inline-flex items-center justify-end border-collapse border border-M-text-color/20 rounded-md overflow-hidden">
                    <li>
                        <button className="table-pagination-item disabled:opacity-50" onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}><MdKeyboardArrowLeft /></button>
                    </li>
                    {[...Array(totalPages)].map((_, i) => (
                        <li key={i}>
                            <button 
                                className={`table-pagination-item ${currentPage === i + 1 ? "bg-M-primary-color text-white" : ""}`} 
                                onClick={() => handlePageChange(i + 1)}
                            >
                                {i + 1}
                            </button>
                        </li>
                    ))}
                    <li>
                        <button className="table-pagination-item" onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages}><MdKeyboardArrowRight /></button>
                    </li>
                </ul>
               </div>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      <ConfirmActionModal
        isOpen={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={handleDelete}
        ids={selectedRows}
        entityName="deposit"
        title="Confirm Delete"
        message={`Do you really want to delete ${selectedRows.length > 1 ? "these records" : "this record"}?`}
        confirmText="Delete"
        cancelText="Cancel"
      />
      
      <OfficeDepositModal
        isOpen={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setDepositToEdit(null);
        }}
        onSave={handleSaveDeposit}
        depositToEdit={depositToEdit}
        existingDeposits={deposits}
      />
    </div>
  );
};

export default OfficeDepositList;